import { InfoCircledIcon, LayoutIcon } from '@radix-ui/react-icons';
import moment, { Moment } from 'moment-timezone';
import queryString from 'query-string';
import React, { ReactElement, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';
import { Flex } from 'reflexbox';
import { Collapse, Loader, SimpleTable } from '~/components';
import { FilterConfig, GenericFilter } from '~/components/FilterGeneric';
import { getReasonsOfflineStatus, verifyArrayParameters } from '~/helpers/genericHelper';
import { thousandPointFormat } from '~/helpers/thousandFormatNumber';
import { getUserProfile } from '~/helpers/userProfile';
import { useStateVar } from '~/helpers/useStateVar';
import { apiCall, apiCallDownload } from '~/providers';
import { DeviceStatusKeys } from '~/providers/types/deviceStatus';
import { UnitStatusKey } from '~/providers/types/unit';
import { colors } from '~/styles/colors';
import { FilterStateRoute } from '../../NewOverview/helpers/filters';
import { AnalysisLayout } from '../AnalysisLayout';
import { Headers2 } from '../Header';
import { ReturnIframe } from './biFormated';
import {
  BigNumberSub,
  BigNumberTitle,
  BoxHover,
  ButtonExport, Container, ContainerDate, ContainerTooltip, Scroll, TextItalic,
  TooltipContainer,
  TooltipHeader,
} from './styles';

type TabBase = {
  namePage: string;
  url: string;
};

interface UnavailableCausesTableProps {
  loading: boolean,
  data: {
    offline_percentage: string;
    reason: string;
    count: number;
    strong?: boolean | undefined;
  }[],
  columns: {
    name: string;
    value: string;
    accessor: string;
    render: (row: any) => string | JSX.Element;
  }[],
  lastReasonsUpdate?: string;
}

const TABS_BASE: TabBase[] = [
  {
    namePage: 'realTime',
    url: '',
  },
  {
    namePage: 'historic',
    url: 'https://app.powerbi.com/view?r=eyJrIjoiMzI4NzRkNzctNGJmZS00NjE2LTgwY2ItYzQyOTc0NTVkMzQ4IiwidCI6IjNlNmM3M2UzLTkxYzctNGQ2Yy1iZjc1LWNjZDUwOThmM2JhMyJ9',
  },
];

export const AnalysisAvailability = (): JSX.Element => {
  const { t } = useTranslation();
  const profile = getUserProfile();
  const history = useHistory<FilterStateRoute | undefined>();
  const [state, render, setState] = useStateVar({
    unitsCount: 0,
    totalOnline: 0,
    totalOffline: 0,
    selectedStateOpts: [] as any[],
    selectedClientOpts: [] as number[],
    selectedCityOpts: [] as any[],
    selectedUnitOpts: [] as any[],
    selectedUnitStatusOpts: [] as any[],
    selectedDeviceStatusOpts: [] as any[],
    selectedReasonsOpts: [] as any[],
    selectedApplicationsOpts: [] as any[],
    applicationLoading: false,
    lastReasonsUpdate: '',
    applicationsCount: [] as {
        application: string;
        online: number;
        offline: number;
        strong?: boolean;
    }[],
    byHvac: {
      hvac: {
        on: 0,
        off: 0,
      },
      others: {
        on: 0,
        off: 0,
      },
    } as {
      hvac: { on: number, off: number },
      others: { on: number, off: number },
    },
    offlineCountByReason: [] as {
      reason: string,
      count: number,
      strong?: boolean,
    }[],
  });
  const queryPars = queryString.parse(history.location.search);
  const linkBase = history.location.pathname;

  const canAccessHistoric = profile.permissions.PER_CLIENT.some((c) => c.clientId === 145);

  const tabsRefs = useMemo(() => TABS_BASE.map(() => React.createRef<HTMLAnchorElement>()), []);

  const defaultType = TABS_BASE[0].namePage;

  const currentType = (typeof queryPars.type === 'string' && queryPars.type) || defaultType;
  const titles = useMemo(
    () => ({
      realTime: t('tempoReal'),
      historic: t('historico'),
    }),
    [t],
  );
  const allTabs = useMemo(() => TABS_BASE.map((tab, idx) => {
    const link = `${linkBase}?${queryString.stringify({
      ...queryPars,
      type: tab.namePage,
    })}`;
    const title = titles[tab.namePage as keyof typeof titles] ?? tab.namePage;

    return {
      title,
      label: title,
      link,
      isActive: canAccessHistoric ? currentType === tab.namePage : tab.namePage === 'realTime',
      visible: tab.namePage === 'historic' ? canAccessHistoric : true,
      ref: tabsRefs[idx],
      url: tab.url,
      namePage: tab.namePage,
    };
  }),
  [canAccessHistoric, currentType, linkBase, queryPars, tabsRefs, titles]);

  const activeTab = allTabs.find((t) => t.isActive);

  function padronizeHvac() {
    if (!state.byHvac) return [];

    const { hvac, others } = state.byHvac;

    const totalOn = hvac.on + others.on;
    const totalOff = hvac.off + others.off;
    const totalCount = totalOn + totalOff;

    return [
      {
        name: t('dispositivosHvac'),
        on: hvac.on,
        off: hvac.off,
        count: hvac.on + hvac.off,
        strong: false,
      },
      {
        name: t('outrosDispositivos'),
        on: others.on,
        off: others.off,
        count: others.on + others.off,
        strong: false,
      },
      {
        name: t('total'),
        on: totalOn,
        off: totalOff,
        count: totalCount,
        strong: true,
      },
    ];
  }

  const applyFilters = () => {
    getConnectivityOverview();
  };

  const filters = [
    {
      type: 'select',
      key: 'client',
      label: 'Cliente',
      placeholder: 'Cliente',
      multiple: false,
      isVisible: profile.manageAllClients,
    },
    {
      type: 'multi-select',
      key: 'state',
      label: 'Estado',
      placeholder: 'Estado',
      multiple: true,
      isVisible: true,
    },
    {
      type: 'multi-select',
      key: 'city',
      label: 'Cidade',
      placeholder: 'Cidade',
      multiple: true,
      isVisible: true,
    },
    {
      type: 'multi-select',
      key: 'unit',
      label: 'Unidade',
      placeholder: 'Unidade',
      multiple: true,
      isVisible: true,
    },
    {
      type: 'multi-select',
      key: 'unitStatus',
      label: 'Status da Unidade',
      placeholder: 'Status da Unidade',
      multiple: true,
      isVisible: true,
    },
    {
      type: 'multi-select',
      key: 'deviceStatus',
      label: 'Status do Dispositivo',
      placeholder: 'Status do Dispositivo',
      multiple: true,
      isVisible: true,
    },
    {
      type: 'multi-select',
      key: 'application',
      label: 'Aplicação',
      placeholder: 'Aplicação',
      multiple: true,
      isVisible: true,
    },
    {
      type: 'multi-select',
      key: 'reasons',
      label: 'Causa',
      placeholder: 'Causa',
      multiple: true,
      isVisible: canAccessHistoric,
    },
  ] as FilterConfig[];

  const updateCheckInformations = (topic: string, newValues?: any, dates?: Moment | null) => {
    switch (topic) {
      case 'client':
        setState({ selectedClientOpts: Array.isArray(newValues as unknown[]) ? newValues.map(Number) : [Number(newValues)] });
        break;
      case 'state':
        setState({ selectedStateOpts: newValues.map((item) => Number(item)) });
        break;
      case 'city':
        setState({ selectedCityOpts: newValues.map((item) => item) });
        break;
      case 'unit':
        setState({ selectedUnitOpts: newValues.map((item) => Number(item)) });
        break;
      case 'unitStatus':
        setState({ selectedUnitStatusOpts: newValues.map((item) => item) });
        break;
      case 'deviceStatus':
        setState({ selectedDeviceStatusOpts: newValues.map((item) => item) });
        break;
      case 'reasons':
        setState({ selectedReasonsOpts: newValues.map((item) => item) });
        break;
      case 'application':
        setState({ selectedApplicationsOpts: newValues.map((item) => item) });
        break;
      default:
        break;
    }
    render();
  };

  const getUnitStatusParams = () => {
    let unitStatus = verifyArrayParameters(state.selectedUnitStatusOpts) as UnitStatusKey[];

    if (!unitStatus?.length) {
      unitStatus = ['OPERATION', 'MODERNIZATION', 'FORCED_UNINSTALL'];

      if (profile.manageAllClients) {
        unitStatus.push('INSTALLATION');
      }
    }

    return unitStatus;
  };

  const getDeviceStatusParams = () => {
    let deviceStatus = verifyArrayParameters(state.selectedDeviceStatusOpts) as DeviceStatusKeys[];

    if (!deviceStatus?.length) {
      deviceStatus = ['OPERATION', 'MACHINE_WITHOUT_ENERGY', 'FORCED_UNINSTALL'];
    }

    return deviceStatus;
  };

  async function getConnectivityOverview() {
    try {
      setState({ applicationLoading: false });
      const {
        onlineStatusByApplication, byHvac, offlineCountByReason, totalUnits, totalOffline, totalOnline, lastReasonsUpdate,
      } = await apiCall('/get-devices-connectivity-by-application', {
        clientIds: state.selectedClientOpts.length > 0 ? state.selectedClientOpts : undefined,
        cityIds: state.selectedCityOpts.length > 0 ? state.selectedCityOpts : undefined,
        stateIds: state.selectedStateOpts.length > 0 ? state.selectedStateOpts : undefined,
        unitIds: state.selectedUnitOpts.length > 0 ? state.selectedUnitOpts : undefined,
        INCLUDE_UNIT_STATUS: getUnitStatusParams(),
        INCLUDE_DEVICE_STATUS: getDeviceStatusParams(),
        applications: state.selectedApplicationsOpts.length > 0 ? state.selectedApplicationsOpts : undefined,
        reasons: state.selectedReasonsOpts.length > 0 ? state.selectedReasonsOpts : undefined,
      });
      const sortedApplications = [...onlineStatusByApplication].sort((a, b) => {
        const totalA = (a.offline ?? 0) + (a.online ?? 0);
        const totalB = (b.offline ?? 0) + (b.online ?? 0);
        return totalB - totalA;
      });
      const sortedOfflineCountByReason = [...offlineCountByReason].sort(
        (a, b) => (b.count ?? 0) - (a.count ?? 0),
      );
      setState({
        lastReasonsUpdate,
        applicationLoading: true,
        applicationsCount: [
          ...sortedApplications.filter((item) => !(item.offline === 0 && item.online === 0)),
          {
            application: t('total'),
            offline: onlineStatusByApplication.reduce((sum, row) => sum + row.offline, 0),
            online: onlineStatusByApplication.reduce((sum, row) => sum + row.online, 0),
            strong: true,
          },
        ],
        byHvac,
        offlineCountByReason: [
          ...sortedOfflineCountByReason.filter((item) => item.count !== 0),
          {
            count: offlineCountByReason.reduce((sum, row) => sum + row.count, 0),
            reason: t('total'),
            strong: true,
          },
        ],
        unitsCount: totalUnits,
        totalOffline,
        totalOnline,
      });
      render();
    } catch (err) {
      console.log(err);
      toast.error(t('erroDEVS'));
    } finally {
      render();
    }
  }

  const TABLE_COLUMNS_CATEGORIES = [
    {
      name: 'name',
      value: t('tipoDeDispositivo'),
      accessor: 'name',
      maxWidth: '150px',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {row.name}
            </strong>
          );
        }
        return row.name;
      },
    },
    {
      name: 'count',
      value: t('quantidadeDeDispositivos'),
      accessor: 'count',
      maxWidth: '50px',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {thousandPointFormat(row.count)}
            </strong>
          );
        }
        return thousandPointFormat(row.count);
      },
    },
    {
      name: 'on',
      value: t('dispositivosOnline'),
      accessor: 'on',
      maxWidth: '50px',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {thousandPointFormat(row.on)}
            </strong>
          );
        }
        return thousandPointFormat(row.on);
      },
    },
    {
      name: 'devs_online',
      value: `${t('dispositivosOnline')} (%)`,
      accessor: 'devs_online',
      maxWidth: '50px',
      render: (row) => {
        const count = Number(row.count) || 0;
        const on = Number(row.on) || 0;
        const percentage = count > 0 ? ((on / count) * 100).toFixed(1) : '0.0';
        if (row.strong === true) {
          return (
            <strong>
              <TextItalic>
                {percentage}
                %
              </TextItalic>
            </strong>
          );
        } return (
          <TextItalic>
            {percentage}
            %
          </TextItalic>
        );
      },
    },
    {
      name: 'off',
      value: t('dispositivosOffline'),
      accessor: 'off',
      maxWidth: '50px',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {thousandPointFormat(row.off)}
            </strong>
          );
        }
        return thousandPointFormat(row.off);
      },
    },
    {
      name: 'devs_offline',
      value: `${t('dispositivosOffline')} (%)`,
      accessor: 'devs_offline',
      maxWidth: '50px',
      render: (row) => {
        const count = Number(row.count) || 0;
        const off = Number(row.off) || 0;
        const percentage = count > 0 ? ((off / count) * 100).toFixed(1) : '0.0';
        if (row.strong === true) {
          return (
            <strong>
              <TextItalic>
                {percentage}
                %
              </TextItalic>
            </strong>
          );
        } return (
          <TextItalic>
            {percentage}
            %
          </TextItalic>
        );
      },
    },
  ];

  const TABLE_COLUMNS_APPLICATION = [
    {
      name: 'application',
      value: t('aplicacao'),
      accessor: 'application',
      maxWidth: '150px',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {row.application}
            </strong>
          );
        }
        return row.application;
      },
    },
    {
      name: 'count',
      value: t('quantidadeDeDispositivos'),
      accessor: 'count',
      maxWidth: '50px',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {thousandPointFormat(row.offline + row.online)}
            </strong>
          );
        }
        return thousandPointFormat(row.offline + row.online);
      },
    },
    {
      name: 'online',
      value: t('dispositivosOnline'),
      accessor: 'online',
      maxWidth: '50px',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {thousandPointFormat(row.online)}
            </strong>
          );
        }
        return thousandPointFormat(row.online);
      },
    },
    {
      name: 'online_percentage',
      value: `${t('dispositivosOnline')} (%)`,
      accessor: 'online_percentage',
      maxWidth: '50px',
      render: (row) => {
        const total = row.online + row.offline;
        const percentage = total > 0 ? ((row.online / total) * 100).toFixed(1) : '0.0';
        if (row.strong === true) {
          return (
            <strong>
              <TextItalic>
                {percentage}
                %
              </TextItalic>
            </strong>
          );
        } return (
          <TextItalic>
            {percentage}
            %
          </TextItalic>
        );
      },
    },
    {
      name: 'offline',
      value: t('dispositivosOffline'),
      accessor: 'offline',
      maxWidth: '50px',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {thousandPointFormat(row.offline)}
            </strong>
          );
        }
        return thousandPointFormat(row.offline);
      },
    },
    {
      name: 'offline_percentage',
      value: `${t('dispositivosOffline')} (%)`,
      accessor: 'offline_percentage',
      maxWidth: '50px',
      render: (row) => {
        const total = row.online + row.offline;
        const percentage = total > 0 ? ((row.offline / total) * 100).toFixed(1) : '0.0';
        if (row.strong === true) {
          return (
            <strong>
              <TextItalic>
                {percentage}
                %
              </TextItalic>
            </strong>
          );
        } return (
          <TextItalic>
            {percentage}
            %
          </TextItalic>
        );
      },
    },
  ];

  const reasons = getReasonsOfflineStatus();
  function countTotalReason() {
    const totalDevs = state.totalOffline + state.totalOnline;

    return totalDevs;
  }

  const dataWithPercentagesReasons = state.offlineCountByReason.map((item) => ({
    ...item,
    offline_percentage: countTotalReason() > 0
      ? `${((item.count / countTotalReason()) * 100).toFixed(1)}%`
      : '0.0',
  }));
  const TABLE_COLUMNS_CAUSES = [
    {
      name: 'reason',
      value: t('causas'),
      accessor: 'reason',
      render: (row) => {
        const reason = reasons.find((r) => r.nome === row.reason);

        return (
          <>
            <ReactTooltip
              id={row.reason}
              place="top"
              textColor={colors.White}
              backgroundColor={colors.GreyDark}
              className="boxShadow"
            >
              <TooltipContainer>
                <TooltipHeader>
                  {row.reason}
                </TooltipHeader>
                <ContainerTooltip>
                  {reason?.descricao}
                </ContainerTooltip>
              </TooltipContainer>
            </ReactTooltip>
            <Flex style={{ gap: '5px', alignItems: 'center' }}>
              {row.strong ? (
                <strong>
                  {row.reason}
                </strong>
              ) : (<div>{row.reason}</div>)}
              {reason?.descricao && (
                <BoxHover data-tip data-for={row.reason}>
                  <InfoCircledIcon
                    color={colors.Grey300}
                  />
                </BoxHover>
              )}
            </Flex>
          </>
        );
      },
    },
    {
      name: 'count',
      value: t('dispositivosOffline'),
      accessor: 'count',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              {thousandPointFormat(row.count)}
            </strong>
          );
        }
        return thousandPointFormat(row.count);
      },
    },
    {
      name: 'offline_percentage',
      value: `${t('dispositivosOffline')} (%)`,
      accessor: 'offline_percentage',
      render: (row) => {
        if (row.strong === true) {
          return (
            <strong>
              <TextItalic>
                {row.offline_percentage}
              </TextItalic>
            </strong>
          );
        } return (
          <TextItalic>
            {row.offline_percentage}
          </TextItalic>
        );
      },
    },
  ];

  const now = new Date();
  const formattedDate = `${now.getDate().toString().padStart(2, '0')}/${
    (now.getMonth() + 1).toString().padStart(2, '0')
  }/${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${
    now.getMinutes().toString().padStart(2, '0')
  }`;

  async function exportData() {
    toast.success(t('mensagemDeExportacaoIniciada'));
    try {
      const resp = await apiCallDownload('/get-devices-connectivity-export', {
        clientIds: state.selectedClientOpts.length > 0 ? state.selectedClientOpts : undefined,
        INCLUDE_UNIT_STATUS: getUnitStatusParams(),
        INCLUDE_DEVICE_STATUS: getDeviceStatusParams(),
        cityIds: state.selectedCityOpts.length > 0 ? state.selectedCityOpts : undefined,
        stateIds: state.selectedStateOpts.length > 0 ? state.selectedStateOpts : undefined,
        unitIds: state.selectedUnitOpts.length > 0 ? state.selectedUnitOpts : undefined,
      });
      generateDownloadFileLink(
        resp.data,
        resp.headers.filename ?? 'Disponibilidade.xlsx',
      );

      toast.success(t('sucessoExportacao'));
    } catch (err) {
      console.error(err);
      toast.error(t('erroAoExportar'));
    }
  }

  function generateDownloadFileLink(file: Blob, fileName: string): void {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(file);
    link.download = fileName;

    link.click();
    link.remove();
  }

  useEffect(() => {
    if (history.location.state) {
      const filterState = history.location.state;

      if (filterState.client) {
        state.selectedClientOpts = [Number(filterState.client)];
      }

      if (filterState.states?.length) {
        state.selectedStateOpts = filterState.states.map((item) => Number(item));
      }

      if (filterState.units?.length) {
        state.selectedUnitOpts = filterState.units.map((item) => Number(item));
      }

      render();
      history.location.state = undefined;
    }

    getConnectivityOverview();
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('dielEnergiaAnaliseDisponibilidade')}</title>
      </Helmet>
      <AnalysisLayout />
      <div style={{ paddingTop: '10px' }}>
        <Headers2 links={allTabs} />
      </div>
      {
        activeTab?.namePage === 'realTime' && (
          <>
            <GenericFilter
              state={state}
              filters={filters}
              onApply={applyFilters}
              updateInfos={updateCheckInformations}
            />
            <Container>
              { !state.applicationLoading
                ? (
                  <Loader variant="primary" size="large" />
                )
                : (
                  <>
                    <Flex flexDirection="column" justifyContent="center" alignItems="center">
                      <BigNumberTitle>
                        {t('unidades')}
                      </BigNumberTitle>
                      <BigNumberSub>
                        {state.unitsCount}
                      </BigNumberSub>
                    </Flex>
                    <Flex flexDirection="column" justifyContent="center" alignItems="center">
                      <BigNumberTitle>
                        {t('dispositivos')}
                      </BigNumberTitle>
                      <BigNumberSub>
                        {thousandPointFormat(state.totalOffline + state.totalOnline)}
                      </BigNumberSub>
                    </Flex>
                    <Flex flexDirection="column" justifyContent="center" alignItems="center">
                      <BigNumberTitle>
                        {t('online')}
                      </BigNumberTitle>
                      <BigNumberSub>
                        {thousandPointFormat(state.totalOnline)}
                      </BigNumberSub>
                    </Flex>
                    <Flex flexDirection="column" justifyContent="center" alignItems="center">
                      <BigNumberTitle>
                        {t('offline')}
                      </BigNumberTitle>
                      <BigNumberSub>
                        {thousandPointFormat(state.totalOffline)}
                      </BigNumberSub>
                    </Flex>
                  </>
                )}
            </Container>
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: '10px' }}>
              <ButtonExport onClick={() => exportData()}>
                <LayoutIcon style={{ marginRight: '5px' }} />
                {t('exportarPlanilha')}
              </ButtonExport>
            </div>
            <Collapse title={t('categorias')}>
              {!state.applicationLoading ? (
                <Loader variant="primary" size="large" />
              ) : (
                <>
                  <Scroll>
                    <div style={{ minWidth: '1000px' }}>
                      <SimpleTable
                        data={padronizeHvac()}
                        columns={TABLE_COLUMNS_CATEGORIES}
                      />
                    </div>
                  </Scroll>
                  <ContainerDate>
                    {`${`${t('ultimaAtualizacao')}: `}${formattedDate}`}
                  </ContainerDate>
                </>
              )}
            </Collapse>
            <Collapse title={t('aplicacao')}>
              {!state.applicationLoading ? (
                <Loader variant="primary" size="large" />
              ) : (
                <>
                  <Scroll>
                    <div style={{ minWidth: '1000px' }}>
                      <SimpleTable
                        data={state.applicationsCount}
                        columns={TABLE_COLUMNS_APPLICATION}
                      />
                    </div>
                  </Scroll>
                  <ContainerDate>
                    {`${`${t('ultimaAtualizacao')}: `}${formattedDate}`}
                  </ContainerDate>
                </>
              )}

            </Collapse>
            <Collapse title={t('causasDaIndisponibilidade')}>
              <CausesTable
                columns={TABLE_COLUMNS_CAUSES}
                data={dataWithPercentagesReasons}
                loading={!state.applicationLoading}
                lastReasonsUpdate={state.lastReasonsUpdate}
              />
            </Collapse>
          </>
        )
      }
      {
        activeTab?.namePage === 'historic' && (
          <ReturnIframe url={activeTab.url} height={800} />
        )
      }
    </>
  );
};

function CausesTable({
  data,
  loading,
  columns,
  lastReasonsUpdate,
}: Readonly<UnavailableCausesTableProps>): ReactElement {
  const { t } = useTranslation();
  const timeZoneArea = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (loading) {
    return (
      <Loader variant="primary" size="large" />
    );
  }

  return (
    <>
      <Scroll style={{ overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap' }}>
        <SimpleTable
          data={data}
          columns={columns}
        />
      </Scroll>
      <ContainerDate>
        {lastReasonsUpdate ? `${t('ultimaAtualizacao')}: ${moment.utc(lastReasonsUpdate).tz(timeZoneArea).format('DD/MM/YYYY HH:mm')}` : null}
      </ContainerDate>
    </>
  );
}
